<template>
  <div v-style:bg="content.backgroundColor">
    <cms-recommendation-carousel
      :class="{ container: !content.isFullWidth }"
      :currency="currency[locale]"
      :loading
      :products="recommendedProducts"
      :quickshop
      :title="content.title"
      :title-color="content.titleColor"
      :title-style="content.titleStyle"
      :variant="content.variant"
      @product-click="handleProductClick"
    />
  </div>
</template>

<script lang="ts" setup>
import type { BloomreachRecommendations } from '#types/components/cms/bloomreach-recommendations'
import type { LayoutVariant } from '#types/components/cms/recommendation-carousel'
import { getPageInfo } from '#commerce/gtm/page'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'
import { extractBloomReachProduct } from '#core/utils/p13n'

const { content } = defineProps<{
  content: BloomreachRecommendations
}>()

const {
  bloomreachIndex,
  cardTextColor: textColor,
  ctaStyle,
  originalPriceColor,
  quickShopVisibility,
  salePriceColor,
  showRatings,
  textLinkColor,
  textLinkHoverColor,
  textLinkStyle,
  textLinkUnderline,
  title,
  whiteBackground
} = content

provide(RecommendationStyleContextKey, {
  ctaStyle,
  originalPriceColor,
  salePriceColor,
  textColor,
  textLinkColor,
  textLinkHoverColor,
  textLinkStyle,
  textLinkUnderline,
  whiteBackground
})

const variantOverride = inject('variantOverride', undefined) as LayoutVariant | undefined

const { enableQuickShop } = useAppConfig().components.cms.productRecommendations
const locale = useLocale()
const { handleBloomreachProductClick } = useMonetate()
const { $gtm } = useNuxtApp()
const route = useRoute()
const { currency } = useRuntimeConfig().public

const loading = ref(true)
const recommendedProducts = ref<Awaited<ReturnType<typeof extractBloomReachProduct>>>([])

const [pageCategory] = getPageInfo(route, {})
const quickshop = quickShopVisibility ? quickShopVisibility === 'visible' : enableQuickShop

const getRecommendedProducts = (products = []) =>
  products.map((product) => extractBloomReachProduct(product, showRatings))

const handleProductClick = (product, position: number) => {
  handleBloomreachProductClick(product, position, title, route.path)
}

const loadProducts = () => {
  const products = window[variantOverride === 'search-hub' ? 'vfrecs' : 'vfbr']?.[bloomreachIndex || 0] || []
  recommendedProducts.value = getRecommendedProducts(products)
  loading.value = false
}

watchOnce(() => recommendedProducts.value?.length, () => {
  const productItems = recommendedProducts.value.map((product) => ({
    ...product,
    price: +product.price.highOriginal,
    salePrice: +product.price.highCurrent
  }))

  $gtm.push('product.onProductRecImpressions', productItems, {
    currency: currency[locale],
    list: `${pageCategory || 'Home'} | Product Recommendation Carousel`,
    title
  })
})

onMounted(() => {
  loadProducts()
  window.addEventListener('recalculateBloomreachVfa', loadProducts)
})

onBeforeUnmount(() => {
  window.removeEventListener('recalculateBloomreachVfa', loadProducts)
})
</script>
